import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ConfirmDialog from '@/components/ConfirmDialog';
import CalculationBreakdown from '@/components/CalculationBreakdown';
import SheetImposition from '@/components/SheetImposition';
import api from '@/lib/api';
import { DEFAULT_PRODUCTION_PRIORITY, PRODUCTION_PRIORITIES } from '@/constants/production';
import '@/pages/Products.css';
import '@/pages/Clients.css';
import '@/components/SheetImposition.css';

const formatMoney = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PRODUCT_CALCULATOR_REVISTA = 'revista';
const PRODUCT_CALCULATOR_CUADERNO = 'cuaderno';
const PRODUCT_CALCULATOR_FACTURAS = 'facturas';
const INVOICE_COPY_ORDER = [
  { label: 'Original', suffix: 'original' },
  { label: 'Duplicado', suffix: 'duplicado' },
  { label: 'Triplicado', suffix: 'triplicado' },
  { label: 'Cuatriplicado', suffix: 'cuatriplicado' },
];
const PDF_PAYMENT_TERMS = [
  '100% Anticipado',
  '50% Anticipado',
  '7 Dias',
  '15 Dias',
  '30 Dias',
];
const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const renderValue = (variable, rawValue) => {
  if (variable.type === 'number') return rawValue;
  if (variable.type === 'select') return rawValue;
  if (variable.type === 'checkbox') return rawValue ? 'Sí' : 'No';
  if (variable.type === 'calculated') return 'Calculada';
  return String(rawValue);
};

const normalizeMagazineElements = (config = {}) => (
  (config.elements || []).filter(element => element?.page_count_variable)
);

const isPageCalculatorBudget = (budget) => (
  budget?.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_REVISTA
  || budget?.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
);

const DEFAULT_COVER_CONFIG = {
  enabled_variable: 'tienetapa',
  front_colors_variable: 'colortapafrente',
  back_colors_variable: 'colortapadorso',
  plates_total_variable: 'chapastapa',
  print_sheets_variable: 'pliegostapa',
  mother_sheet_variable: 'hojamadretapa',
  spoilage_variable: 'desperdiciotapa',
  sheet_width_variable: 'anchohojatapa',
  sheet_height_variable: 'largohojatapa',
};

const getCoverConfig = (budget) => ({
  ...DEFAULT_COVER_CONFIG,
  ...(budget?.product_snapshot?.calculator_config?.cover || {}),
});

const hasCover = (budget) => {
  const cover = getCoverConfig(budget);
  return Boolean(isPageCalculatorBudget(budget) && cover.enabled && getResolved(budget, cover.enabled_variable) > 0);
};

const getMagazineElementKey = (element, index) => element.page_count_variable || `interior-${index + 1}`;

const getResolved = (budget, key) => {
  if (!key) return 0;
  return Number(budget?.resolved_values?.[key] ?? budget?.variable_values?.[key] ?? 0) || 0;
};

const getInvoiceCopies = (budget) => {
  const config = budget?.product_snapshot?.calculator_config || {};
  const configuredCopies = Array.isArray(config.copies) ? config.copies : [];
  if (configuredCopies.length > 0) {
    return configuredCopies.map((copy, index) => ({
      ...copy,
      key: copy.key || INVOICE_COPY_ORDER[index]?.suffix || `copia-${index + 1}`,
      label: copy.label || INVOICE_COPY_ORDER[index]?.label || `Copia ${index + 1}`,
    }));
  }
  return INVOICE_COPY_ORDER.map(copy => ({
    key: copy.suffix,
    label: copy.label,
    paper_variable: `papel${copy.suffix}`,
    grammage_variable: `gramaje${copy.suffix}`,
    colors_variable: `colores${copy.suffix}`,
    plates_variable: `chapas${copy.suffix}`,
  }));
};

const getInvoiceCopyCount = (budget) => {
  const config = budget?.product_snapshot?.calculator_config || {};
  const key = config.copy_count_variable || 'tipofactura';
  const rawValue = budget?.resolved_values?.[key] ?? budget?.variable_values?.[key] ?? 1;
  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return Math.max(1, Math.min(INVOICE_COPY_ORDER.length, Math.round(numericValue)));
  }
  const normalized = String(rawValue || '').trim().toLowerCase();
  const matchedIndex = INVOICE_COPY_ORDER.findIndex(copy => copy.label.toLowerCase() === normalized);
  return matchedIndex >= 0 ? matchedIndex + 1 : 1;
};

const getActiveInvoiceCopies = (budget) => (
  getInvoiceCopies(budget).slice(0, getInvoiceCopyCount(budget))
);

const getVariableDisplayValue = (budget, key) => {
  const variables = [
    ...(budget?.product_snapshot?.global_variables || []),
    ...(budget?.product_snapshot?.variables || []),
  ];
  const variable = variables.find(v => v.key === key);
  const raw = budget?.variable_values?.[key];
  if (!variable) return raw || '-';
  if (variable.type === 'select') return raw || '-';
  return raw ?? (getResolved(budget, key) || '-');
};

const BudgetDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [budget, setBudget] = useState(null);
  const [isInProduction, setIsInProduction] = useState(false);
  const [productionOrder, setProductionOrder] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [productionDialogOpen, setProductionDialogOpen] = useState(false);
  const [productionWorkerId, setProductionWorkerId] = useState('');
  const [productionPriority, setProductionPriority] = useState(DEFAULT_PRODUCTION_PRIORITY);
  const [sendingProduction, setSendingProduction] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfAction, setPdfAction] = useState('download');
  const [pdfOptionsLoading, setPdfOptionsLoading] = useState(false);
  const [relatedPdfBudgets, setRelatedPdfBudgets] = useState([]);
  const [selectedPdfBudgetIds, setSelectedPdfBudgetIds] = useState([]);
  const [pdfExtraDetails, setPdfExtraDetails] = useState({});
  const [pdfExtraEditorsOpen, setPdfExtraEditorsOpen] = useState({});
  const [pdfIncludeVat, setPdfIncludeVat] = useState(false);
  const [pdfPaymentTerms, setPdfPaymentTerms] = useState(PDF_PAYMENT_TERMS[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [{ data }, productionRes] = await Promise.all([
        api.get(`/budgets/${id}`),
        api.get('/production-orders'),
      ]);
      setBudget(data);
      const linkedOrder = (productionRes.data || []).find(order => order.budget_id === id) || null;
      setProductionOrder(linkedOrder);
      setIsInProduction(!!linkedOrder);
    } catch (err) {
      setError('Presupuesto no encontrado');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await api.delete(`/budgets/${id}`);
      navigate(budget.status === 'approved' ? '/presupuestos-aprobados' : '/presupuestos');
    } catch (err) {
      alert('Error al eliminar');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    setStatusConfirm(status);
  };

  const confirmStatusChange = async () => {
    if (!statusConfirm) return;
    try {
      setStatusLoading(true);
      const { data } = await api.put(`/budgets/${id}/status`, { status: statusConfirm });
      setBudget(data);
      setStatusConfirm(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al actualizar presupuesto');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSendToProduction = async () => {
    try {
      const { data } = await api.get('/workers');
      setWorkers(data || []);
      setProductionWorkerId(data?.[0]?.id || '');
      setProductionPriority(DEFAULT_PRODUCTION_PRIORITY);
      setProductionDialogOpen(true);
    } catch (err) {
      alert('Error al cargar trabajadores');
    }
  };

  const confirmSendToProduction = async () => {
    if (!productionWorkerId) {
      alert('Selecciona un trabajador');
      return;
    }
    try {
      setSendingProduction(true);
      const { data } = await api.post(`/budgets/${id}/send-to-production`, {
        assigned_worker_id: productionWorkerId,
        priority: productionPriority,
      });
      if (data?.id && data.priority !== productionPriority) {
        await api.put(`/production-orders/${data.id}`, {
          priority: productionPriority,
        });
      }
      setProductionDialogOpen(false);
      navigate('/produccion');
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al enviar a produccion');
    } finally {
      setSendingProduction(false);
    }
  };

  const fetchBudgetPdf = async (budgetIds, extraDetails, includeVat, paymentTerms) => {
    const response = await api.post(
      `/budgets/pdf?include_vat=${includeVat ? 'true' : 'false'}`,
      {
        budget_ids: budgetIds,
        extra_details: extraDetails,
        include_vat: includeVat,
        payment_terms: paymentTerms,
      },
      { responseType: 'blob' }
    );
    return new Blob([response.data], { type: 'application/pdf' });
  };

  const openPdfDialog = async (action) => {
    setPdfAction(action);
    setPdfDialogOpen(true);
    setSelectedPdfBudgetIds([id]);
    setPdfExtraDetails({});
    setPdfExtraEditorsOpen({});
    setPdfIncludeVat(false);
    setPdfPaymentTerms(PDF_PAYMENT_TERMS[0]);
    setPdfOptionsLoading(true);
    try {
      const { data } = await api.get(`/budgets?client_id=${budget.client_id}&status=all`);
      const sameClientBudgets = (data || [])
        .filter(item => item.client_id === budget.client_id)
        .filter(item => item.status !== 'approved' || item.id === id);
      setRelatedPdfBudgets(sameClientBudgets);
    } catch (err) {
      setRelatedPdfBudgets([budget]);
      alert('No se pudieron cargar otros presupuestos del cliente');
    } finally {
      setPdfOptionsLoading(false);
    }
  };

  const togglePdfBudget = (budgetId) => {
    setSelectedPdfBudgetIds(current => (
      current.includes(budgetId)
        ? current.filter(item => item !== budgetId)
        : [...current, budgetId]
    ));
  };

  const updatePdfExtraDetail = (budgetId, value) => {
    setPdfExtraDetails(current => ({
      ...current,
      [budgetId]: value,
    }));
  };

  const togglePdfExtraEditor = (budgetId) => {
    setPdfExtraEditorsOpen(current => ({
      ...current,
      [budgetId]: !current[budgetId],
    }));
  };

  const runPdfGeneration = async () => {
    if (selectedPdfBudgetIds.length === 0) {
      alert('Selecciona al menos un presupuesto');
      return;
    }
    let printWindow = null;
    try {
      setPdfLoading(true);
      if (pdfAction === 'print') {
        printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('El navegador bloqueo la ventana de impresion');
          return;
        }
        printWindow.document.write('<p>Generando PDF...</p>');
      }
      const selectedExtraDetails = selectedPdfBudgetIds.reduce((acc, budgetId) => {
        const value = (pdfExtraDetails[budgetId] || '').trim();
        if (value) acc[budgetId] = value;
        return acc;
      }, {});
      const blob = await fetchBudgetPdf(
        selectedPdfBudgetIds,
        selectedExtraDetails,
        pdfIncludeVat,
        pdfPaymentTerms
      );
      const url = window.URL.createObjectURL(blob);
      if (pdfAction === 'print') {
        printWindow.location.href = url;
        printWindow.addEventListener('load', () => {
          printWindow.focus();
          printWindow.print();
        });
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        setPdfDialogOpen(false);
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedPdfBudgetIds.length === 1
        ? `presupuesto-${String(budget.budget_number).padStart(4, '0')}.pdf`
        : `presupuestos-${budget.client_snapshot?.name || 'cliente'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setPdfDialogOpen(false);
    } catch (err) {
      if (printWindow) printWindow.close();
      alert('Error al generar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    openPdfDialog('download');
  };

  const handlePrintPdf = async () => {
    openPdfDialog('print');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar activePath={location.pathname} />
        <main className="main-content">
          <div className="empty-state">Cargando...</div>
        </main>
      </div>
    );
  }

  if (error || !budget) {
    return (
      <div className="dashboard-container">
        <Sidebar activePath={location.pathname} />
        <main className="main-content">
          <div className="login-error">{error || 'No encontrado'}</div>
          <button className="btn-secondary" onClick={() => navigate('/presupuestos')} style={{ marginTop: '1rem' }}>
            Volver
          </button>
        </main>
      </div>
    );
  }

  const variables = [
    ...(budget.product_snapshot?.global_variables || []).map(v => ({ ...v, global: true })),
    ...(budget.product_snapshot?.variables || []),
  ];
  const isApproved = budget.status === 'approved';
  const isDelivered = productionOrder?.status === 'entregado';
  const backPath = isApproved ? '/presupuestos-aprobados' : '/presupuestos';
  const profitAmount = Number(
    budget.profit_amount ?? ((Number(budget.base_cost) || 0) * ((Number(budget.profit_percentage) || 0) / 100))
  );

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="budget-detail-title">
              Presupuesto <span className="mono">#{String(budget.budget_number).padStart(4, '0')}</span>
            </h1>
            <p className="dashboard-subtitle">
              Creado el {formatDate(budget.created_at)} por {budget.created_by}
            </p>
            <p className="dashboard-subtitle">
              Estado: <strong>{isApproved ? 'Aprobado' : 'Pendiente'}</strong>
              {isApproved && budget.approved_at ? ` - aprobado el ${formatDate(budget.approved_at)}` : ''}
              {isApproved && budget.approved_by ? ` por ${budget.approved_by}` : ''}
              {isDelivered ? ' - entregado' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={handleDownloadPdf} disabled={pdfLoading}>
              {pdfLoading ? 'Generando...' : 'Descargar PDF'}
            </button>
            <button className="btn-secondary" onClick={handlePrintPdf} disabled={pdfLoading}>
              Imprimir PDF
            </button>
            <button className="btn-secondary" onClick={() => navigate(backPath)} data-testid="back-button">
              Volver
            </button>
            {isApproved && !isDelivered ? (
              <>
                {isInProduction && productionOrder?.id && (
                  <Link
                    className="btn-secondary"
                    to={`/produccion/${productionOrder.id}`}
                    data-testid="view-production-order-detail"
                  >
                    Ver orden de produccion
                  </Link>
                )}
                {!isInProduction && (
                  <button
                    className="btn-primary-inline"
                    onClick={handleSendToProduction}
                    data-testid="send-to-production-detail"
                  >
                    Enviar a produccion
                  </button>
                )}
                {!isInProduction && (
                  <button
                    className="btn-secondary"
                    onClick={() => handleStatusChange('pending')}
                    data-testid="pending-budget-detail"
                  >
                    Volver a pendiente
                  </button>
                )}
              </>
            ) : !isApproved ? (
              <button
                className="btn-primary-inline"
                onClick={() => handleStatusChange('approved')}
                data-testid="approve-budget-detail"
              >
                Aprobar
              </button>
            ) : null}
            {!isDelivered && (
              <>
                <button className="btn-primary-inline" onClick={() => navigate(`/presupuestos/${id}/editar`)} data-testid="edit-budget-detail">
                  Editar
                </button>
                <button className="btn-danger" onClick={() => setDeleteConfirmOpen(true)} data-testid="delete-budget-detail">
                  Eliminar
                </button>
              </>
            )}
          </div>
        </div>

        {/* ===== CLIENTE ===== */}
        <div className="card editor-card">
          <h3 className="card-title">Cliente</h3>
          <div className="detail-grid">
            <div><strong>Nombre:</strong> {budget.client_snapshot?.name}</div>
            {budget.client_snapshot?.email && <div><strong>Email:</strong> {budget.client_snapshot.email}</div>}
            {budget.client_snapshot?.phone && <div><strong>Teléfono:</strong> {budget.client_snapshot.phone}</div>}
            {budget.client_snapshot?.address && <div><strong>Dirección:</strong> {budget.client_snapshot.address}</div>}
          </div>
        </div>

        {/* ===== PRODUCTO + VARIABLES ===== */}
        <div className="card editor-card">
          <h3 className="card-title">{budget.product_snapshot?.name}</h3>
          {budget.product_snapshot?.description && (
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>{budget.product_snapshot.description}</p>
          )}

          {variables.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Variable</th>
                  <th>Valor ingresado</th>
                  <th style={{ textAlign: 'right' }}>Valor numérico</th>
                </tr>
              </thead>
              <tbody>
                {variables.map(v => (
                  <tr key={`${v.global ? 'global' : 'product'}-${v.key}`} data-testid={`budget-var-row-${v.key}`}>
                    <td className="cell-strong">
                      {v.label || v.key} {v.global && <span className="unit-hint">(global)</span>} {v.show_in_budget === false && <span className="unit-hint">(oculta)</span>} {v.unit && <span className="unit-hint">({v.unit})</span>}
                    </td>
                    <td>{renderValue(v, budget.variable_values?.[v.key])}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {budget.resolved_values?.[v.key] !== undefined
                        ? Number(budget.resolved_values[v.key]).toLocaleString('es-AR')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="formula-snapshot">
            <div className="formula-label">Fórmula usada:</div>
            <code className="formula-code mono">{budget.product_snapshot?.formula}</code>
          </div>
        </div>

        {/* ===== TOTALES ===== */}
        <div className="card editor-card">
          <h3 className="card-title">Desglose de Costos</h3>
          <div className="test-result">
            <div className="test-result-row">
              <span>Costo base:</span>
              <span className="mono" data-testid="detail-base-cost">{formatMoney(budget.base_cost)}</span>
            </div>
            <div className="test-result-row">
              <span>Ganancia ({budget.profit_percentage}%):</span>
              <span className="mono">{formatMoney(profitAmount)}</span>
            </div>
            <div className="test-result-row test-result-final">
              <span>Precio final:</span>
              <span className="mono" data-testid="detail-final-price">{formatMoney(budget.final_price)}</span>
            </div>
            <CalculationBreakdown
              preview={budget}
              variables={variables}
              formula={budget.product_snapshot?.formula}
            />
          </div>
        </div>

        {budget.production_time_estimate && (
          <div className="card editor-card">
            <h3 className="card-title">Tiempo de Produccion Estimado</h3>
            <div className="test-result">
              <div className="test-result-row test-result-final">
                <span>Variable tiempo:</span>
                <span className="mono">
                  {budget.production_time_estimate.total_hours.toLocaleString('es-AR', { maximumFractionDigits: 4 })} h
                </span>
              </div>
              <div className="test-result-row">
                <span>Total estimado:</span>
                <span className="mono">
                  {budget.production_time_estimate.total_minutes.toLocaleString('es-AR', { maximumFractionDigits: 0 })} min
                </span>
              </div>
              <div className="test-result-row">
                <span>Variable tiempo_minutos:</span>
                <span className="mono">
                  {budget.production_time_estimate.total_minutes.toLocaleString('es-AR', { maximumFractionDigits: 2 })} min
                </span>
              </div>
              <div className="test-result-row">
                <span>Preparacion:</span>
                <span className="mono">{budget.production_time_estimate.setup_minutes.toLocaleString('es-AR', { maximumFractionDigits: 0 })} min</span>
              </div>
              <div className="test-result-row">
                <span>Impresion:</span>
                <span className="mono">{budget.production_time_estimate.run_minutes.toLocaleString('es-AR', { maximumFractionDigits: 0 })} min</span>
              </div>
              <div className="test-result-row">
                <span>Cantidad estimada:</span>
                <span className="mono">
                  {budget.production_time_estimate.total_work_units.toLocaleString('es-AR')} pliegos
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ===== IMPOSITION SNAPSHOT ===== */}
        {budget.imposition_choice
          && budget.product_snapshot?.calculator_type !== PRODUCT_CALCULATOR_REVISTA
          && budget.product_snapshot?.calculator_type !== PRODUCT_CALCULATOR_CUADERNO
          && budget.product_snapshot?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS && (
          <div className="card editor-card">
            <h3 className="card-title">Imposición Elegida</h3>
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              <strong>{budget.imposition_choice.label}</strong>
            </p>

            <div className="imposition-option-stats" style={{ maxWidth: '520px', marginBottom: '1.5rem' }}>
              <div className="imposition-stat">
                <div className="imposition-stat-value" data-testid="detail-items-per-sheet">
                  {budget.imposition_choice.items_per_sheet}
                </div>
                <div className="imposition-stat-label">Piezas por pliego</div>
              </div>
              <div className="imposition-stat">
                <div className="imposition-stat-value" data-testid="detail-sheets-needed">
                  {budget.imposition_choice.base_sheets_needed || budget.imposition_choice.sheets_needed}
                </div>
                <div className="imposition-stat-label">Pliegos base</div>
              </div>
              {Number(budget.imposition_choice.spoilage_sheets || 0) > 0 && (
                <div className="imposition-stat">
                  <div className="imposition-stat-value">
                    {budget.imposition_choice.spoilage_sheets}
                  </div>
                  <div className="imposition-stat-label">Desperdicio hojas</div>
                </div>
              )}
              <div className="imposition-stat">
                <div className="imposition-stat-value">
                  {budget.imposition_choice.sheets_needed}
                </div>
                <div className="imposition-stat-label">Pliegos para precio</div>
              </div>
              {Number(budget.imposition_choice.mother_sheets_needed || 0) > 0 && (
                <div className="imposition-stat">
                  <div className="imposition-stat-value">
                    {budget.imposition_choice.mother_sheets_needed}
                  </div>
                  <div className="imposition-stat-label">Hojas madre</div>
                </div>
              )}
              <div className="imposition-stat">
                <div className="imposition-stat-value">
                  {(budget.imposition_choice.usage || 0).toFixed(0)}%
                </div>
                <div className="imposition-stat-label">Aprovechamiento</div>
              </div>
            </div>

            {budget.imposition_choice.pieces && budget.imposition_choice.config && (
              <div className="imposition-preview-wrap">
                <SheetImposition
                  sheetWidth={budget.imposition_choice.config.sheetWidth}
                  sheetHeight={budget.imposition_choice.config.sheetHeight}
                  gripperTop={budget.imposition_choice.config.gripperTop}
                  gripperBottom={budget.imposition_choice.config.gripperBottom}
                  marginLeft={budget.imposition_choice.config.marginLeft}
                  marginRight={budget.imposition_choice.config.marginRight}
                  pieces={budget.imposition_choice.pieces}
                  itemWidth={budget.product_snapshot?.imposition_config?.width_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.width_variable] : 0}
                  itemHeight={budget.product_snapshot?.imposition_config?.height_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.height_variable] : 0}
                  bleed={budget.imposition_choice.config.bleed}
                  side="front"
                  maxHeight={300}
                />
                {budget.imposition_choice.config.doubleSided && (
                  <SheetImposition
                    sheetWidth={budget.imposition_choice.config.sheetWidth}
                    sheetHeight={budget.imposition_choice.config.sheetHeight}
                    gripperTop={budget.imposition_choice.config.gripperTop}
                    gripperBottom={budget.imposition_choice.config.gripperBottom}
                    marginLeft={budget.imposition_choice.config.marginLeft}
                    marginRight={budget.imposition_choice.config.marginRight}
                    pieces={budget.imposition_choice.pieces}
                    itemWidth={budget.product_snapshot?.imposition_config?.width_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.width_variable] : 0}
                    itemHeight={budget.product_snapshot?.imposition_config?.height_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.height_variable] : 0}
                    bleed={budget.imposition_choice.config.bleed}
                    side="back"
                    maxHeight={300}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== OBSERVACIONES ===== */}
        {budget.observations && (
          <div className="card editor-card">
            <h3 className="card-title">Observaciones</h3>
            <p style={{ whiteSpace: 'pre-wrap', color: '#333' }}>{budget.observations}</p>
          </div>
        )}

        {budget.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_FACTURAS && (
          <div className="card editor-card">
            <h3 className="card-title">Imposicion por copia</h3>
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              Cada copia conserva su hoja madre, pliego y diagrama de imposicion.
            </p>
            {getActiveInvoiceCopies(budget).length === 0 ? (
              <div className="empty-state">No hay copias configuradas.</div>
            ) : (
              getActiveInvoiceCopies(budget).map((copy) => {
                const saved = budget.invoice_imposition_choices?.[copy.key];
                const choice = saved?.choice;
                const config = saved?.config;
                if (!choice || !config) {
                  return (
                    <div key={copy.key} className="card editor-card">
                      <h4 className="card-title">{copy.label}</h4>
                      <div className="empty-state">No se guardo una imposicion para esta copia.</div>
                    </div>
                  );
                }
                const sheetsKey = copy.sheets_variable || `pliegos${copy.key}`;
                const motherSheetKey = copy.mother_sheet_variable || `hojamadre${copy.key}`;
                const spoilageKey = copy.spoilage_variable || `desperdicio${copy.key}`;
                const generatedSheetsPerMother = Math.max(1, Number(config?.sourceCut?.sheetCount) || 1);
                const totalSheets = getResolved(budget, sheetsKey);
                const totalMotherSheets = getResolved(budget, motherSheetKey);
                const spoilageMotherSheets = getResolved(budget, spoilageKey);
                const spoilageSheets = spoilageMotherSheets * generatedSheetsPerMother;
                const baseSheets = Number(choice.base_sheets_needed || choice.sheets_needed || 0);
                const baseMotherSheets = Math.max(0, totalMotherSheets - spoilageMotherSheets);
                return (
                  <div key={copy.key} className="card editor-card">
                    <h4 className="card-title">{copy.label}</h4>
                    <div className="detail-grid" style={{ marginBottom: '1rem' }}>
                      <div><strong>Papel:</strong> {getVariableDisplayValue(budget, copy.paper_variable)}</div>
                      <div><strong>Gramaje:</strong> {getResolved(budget, copy.grammage_variable)} gr</div>
                      <div><strong>Colores:</strong> {getResolved(budget, copy.colors_variable)}</div>
                      <div><strong>Chapas:</strong> {getResolved(budget, copy.plates_variable)}</div>
                      <div><strong>Pliegos base:</strong> {baseSheets}</div>
                      <div><strong>Hojas base:</strong> {baseMotherSheets}</div>
                      <div><strong>Desperdicio:</strong> {spoilageMotherSheets} hojas / {spoilageSheets} pliegos</div>
                      <div><strong>Total para precio:</strong> {totalMotherSheets} hojas / {totalSheets} pliegos</div>
                      <div><strong>Hoja:</strong> {getResolved(budget, copy.sheet_width_variable || `anchohoja${copy.key}`)} x {getResolved(budget, copy.sheet_height_variable || `largohoja${copy.key}`)} cm</div>
                    </div>
                    <div className="imposition-option-stats" style={{ maxWidth: '620px', marginBottom: '1.5rem' }}>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{choice.items_per_sheet}</div>
                        <div className="imposition-stat-label">Piezas por pliego</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{baseSheets}</div>
                        <div className="imposition-stat-label">Pliegos originales</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{spoilageSheets}</div>
                        <div className="imposition-stat-label">Pliegos desperdicio</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{totalSheets}</div>
                        <div className="imposition-stat-label">Pliegos para precio</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{(choice.usage || 0).toFixed(0)}%</div>
                        <div className="imposition-stat-label">Aprovechamiento</div>
                      </div>
                    </div>
                    {choice.pieces && config && (
                      <div className="imposition-preview-wrap">
                        <SheetImposition
                          sheetWidth={config.sheetWidth}
                          sheetHeight={config.sheetHeight}
                          gripperTop={config.gripperTop}
                          gripperBottom={config.gripperBottom}
                          marginLeft={config.marginLeft}
                          marginRight={config.marginRight}
                          pieces={choice.pieces}
                          itemWidth={budget.product_snapshot?.imposition_config?.width_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.width_variable] : 0}
                          itemHeight={budget.product_snapshot?.imposition_config?.height_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.height_variable] : 0}
                          bleed={config.bleed}
                          side="front"
                          maxHeight={260}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {isPageCalculatorBudget(budget) && (
          <div className="card editor-card">
            <h3 className="card-title">Imposicion por Interior</h3>
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              Cada interior conserva su hoja madre, pliego y diagrama de imposicion.
            </p>
            {normalizeMagazineElements(budget.calculator_config_override || budget.product_snapshot?.calculator_config || {}).length === 0 ? (
              <div className="empty-state">No hay interiores configurados para este producto.</div>
            ) : (
              normalizeMagazineElements(budget.calculator_config_override || budget.product_snapshot?.calculator_config || {}).map((element, index) => {
                const elementKey = getMagazineElementKey(element, index);
                const saved = budget.magazine_imposition_choices?.[elementKey];
                const choice = saved?.choice || budget.imposition_choice;
                const config = saved?.config || budget.imposition_choice?.config;
                if (!choice || !config) return null;
                const pagesPerSheetKey = element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`;
                const motherSheetKey = element.mother_sheet_variable || `hojamadreint${index + 1}`;
                const sheetWidthKey = element.sheet_width_variable || `anchohojaint${index + 1}`;
                const sheetHeightKey = element.sheet_height_variable || `largohojaint${index + 1}`;
                return (
                  <div key={elementKey} className="card editor-card">
                    <h4 className="card-title">{element.label || `Interior ${index + 1}`}</h4>
                    <div className="detail-grid" style={{ marginBottom: '1rem' }}>
                      <div><strong>Paginas:</strong> {getResolved(budget, element.page_count_variable)}</div>
                      <div><strong>Papel:</strong> {getVariableDisplayValue(budget, index === 0 ? 'papelint1' : 'papelint2')}</div>
                      <div><strong>Colores:</strong> {getResolved(budget, element.front_colors_variable)}/{getResolved(budget, element.back_colors_variable)}</div>
                      <div><strong>Paginas por pliego:</strong> {getResolved(budget, pagesPerSheetKey)}</div>
                      <div><strong>Pliegos distintos:</strong> {getResolved(budget, element.distinct_sheets_variable)}</div>
                      <div><strong>Pliegos para precio:</strong> {getResolved(budget, element.print_sheets_variable)}</div>
                      <div><strong>Hojas madre:</strong> {getResolved(budget, motherSheetKey)}</div>
                      <div><strong>Hoja madre:</strong> {getResolved(budget, sheetWidthKey)} x {getResolved(budget, sheetHeightKey)} cm</div>
                      <div><strong>Planchas:</strong> {getResolved(budget, element.plates_variable)}</div>
                    </div>

                    <div className="imposition-option-stats" style={{ maxWidth: '620px', marginBottom: '1.5rem' }}>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{choice.items_per_sheet}</div>
                        <div className="imposition-stat-label">Piezas por cara</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{choice.base_sheets_needed || choice.sheets_needed}</div>
                        <div className="imposition-stat-label">Pliegos base</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{getResolved(budget, element.print_sheets_variable)}</div>
                        <div className="imposition-stat-label">Pliegos para precio</div>
                      </div>
                      <div className="imposition-stat">
                        <div className="imposition-stat-value">{(choice.usage || 0).toFixed(0)}%</div>
                        <div className="imposition-stat-label">Aprovechamiento</div>
                      </div>
                    </div>

                    {choice.pieces && config && (
                      <div className="imposition-preview-wrap">
                        <SheetImposition
                          sheetWidth={config.sheetWidth}
                          sheetHeight={config.sheetHeight}
                          gripperTop={config.gripperTop}
                          gripperBottom={config.gripperBottom}
                          marginLeft={config.marginLeft}
                          marginRight={config.marginRight}
                          pieces={choice.pieces}
                          itemWidth={budget.product_snapshot?.imposition_config?.width_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.width_variable] : 0}
                          itemHeight={budget.product_snapshot?.imposition_config?.height_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.height_variable] : 0}
                          bleed={config.bleed}
                          side="front"
                          maxHeight={260}
                        />
                        {config.doubleSided && (
                          <SheetImposition
                            sheetWidth={config.sheetWidth}
                            sheetHeight={config.sheetHeight}
                            gripperTop={config.gripperTop}
                            gripperBottom={config.gripperBottom}
                            marginLeft={config.marginLeft}
                            marginRight={config.marginRight}
                            pieces={choice.pieces}
                            itemWidth={budget.product_snapshot?.imposition_config?.width_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.width_variable] : 0}
                            itemHeight={budget.product_snapshot?.imposition_config?.height_variable ? budget.resolved_values?.[budget.product_snapshot.imposition_config.height_variable] : 0}
                            bleed={config.bleed}
                            side="back"
                            maxHeight={260}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {hasCover(budget) && (() => {
          const cover = getCoverConfig(budget);
          const saved = budget.cover_imposition_choice || {};
          const choice = saved.choice;
          const config = saved.config;
          if (!choice || !config) return null;
          const itemWidth = getResolved(budget, 'anchotapa') || getResolved(budget, budget.product_snapshot?.imposition_config?.width_variable);
          const itemHeight = getResolved(budget, 'largotapa') || getResolved(budget, budget.product_snapshot?.imposition_config?.height_variable);
          return (
            <div className="card editor-card">
              <h3 className="card-title">Imposicion de Tapa</h3>
              <div className="detail-grid" style={{ marginBottom: '1rem' }}>
                <div><strong>Papel:</strong> {getVariableDisplayValue(budget, 'papeltapa')}</div>
                <div><strong>Gramaje:</strong> {getResolved(budget, 'gramajetapa')} gr</div>
                <div><strong>Colores:</strong> {getResolved(budget, cover.front_colors_variable)}/{getResolved(budget, cover.back_colors_variable)}</div>
                <div><strong>Pliegos para precio:</strong> {getResolved(budget, cover.print_sheets_variable)}</div>
                <div><strong>Hojas madre:</strong> {getResolved(budget, cover.mother_sheet_variable)}</div>
                <div><strong>Hoja madre:</strong> {getResolved(budget, cover.sheet_width_variable)} x {getResolved(budget, cover.sheet_height_variable)} cm</div>
                <div><strong>Chapas:</strong> {getResolved(budget, cover.plates_total_variable)}</div>
              </div>
              <div className="imposition-preview-wrap">
                <SheetImposition
                  sheetWidth={config.sheetWidth}
                  sheetHeight={config.sheetHeight}
                  gripperTop={config.gripperTop}
                  gripperBottom={config.gripperBottom}
                  marginLeft={config.marginLeft}
                  marginRight={config.marginRight}
                  pieces={choice.pieces}
                  itemWidth={itemWidth}
                  itemHeight={itemHeight}
                  bleed={config.bleed}
                  side="front"
                  maxHeight={260}
                />
                {config.doubleSided && (
                  <SheetImposition
                    sheetWidth={config.sheetWidth}
                    sheetHeight={config.sheetHeight}
                    gripperTop={config.gripperTop}
                    gripperBottom={config.gripperBottom}
                    marginLeft={config.marginLeft}
                    marginRight={config.marginRight}
                    pieces={choice.pieces}
                    itemWidth={itemWidth}
                    itemHeight={itemHeight}
                    bleed={config.bleed}
                    side="back"
                    maxHeight={260}
                  />
                )}
              </div>
            </div>
          );
        })()}

        {pdfDialogOpen && (
          <div className="modal-backdrop" onClick={() => setPdfDialogOpen(false)}>
            <div className="modal pdf-selection-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{pdfAction === 'print' ? 'Imprimir PDF' : 'Descargar PDF'}</h3>
                <button className="btn-remove" onClick={() => setPdfDialogOpen(false)}>x</button>
              </div>
              <div className="modal-body">
                <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                  Selecciona los presupuestos del mismo cliente que queres incluir en la hoja.
                </p>
                <div className="pdf-tax-mode">
                  <span className="form-label">Modelo</span>
                  <label className={`pdf-tax-option ${!pdfIncludeVat ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="pdfIncludeVat"
                      checked={!pdfIncludeVat}
                      onChange={() => setPdfIncludeVat(false)}
                    />
                    Sin IVA
                  </label>
                  <label className={`pdf-tax-option ${pdfIncludeVat ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="pdfIncludeVat"
                      checked={pdfIncludeVat}
                      onChange={() => setPdfIncludeVat(true)}
                    />
                    Con IVA
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Condicion de pago</label>
                  <select
                    className="form-input"
                    value={pdfPaymentTerms}
                    onChange={(e) => setPdfPaymentTerms(e.target.value)}
                  >
                    {PDF_PAYMENT_TERMS.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
                {pdfOptionsLoading ? (
                  <div className="empty-state">Cargando presupuestos...</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '48px' }}></th>
                        <th style={{ width: '90px' }}>Nro.</th>
                        <th>Producto</th>
                        <th>Fecha</th>
                        <th style={{ width: '115px' }}>Detalle</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatedPdfBudgets.map(item => {
                        const selectedForPdf = selectedPdfBudgetIds.includes(item.id);
                        const hasExtraDetail = Boolean((pdfExtraDetails[item.id] || '').trim());
                        const extraEditorOpen = Boolean(pdfExtraEditorsOpen[item.id]);
                        return (
                          <React.Fragment key={item.id}>
                            <tr>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedForPdf}
                                  onChange={() => togglePdfBudget(item.id)}
                                />
                              </td>
                              <td className="mono cell-strong">#{String(item.budget_number).padStart(4, '0')}</td>
                              <td>{item.product_snapshot?.name || '-'}</td>
                              <td>{formatDate(item.created_at)}</td>
                              <td>
                                <button
                                  type="button"
                                  className={`btn-link pdf-detail-toggle ${hasExtraDetail ? 'has-extra' : ''}`}
                                  onClick={() => togglePdfExtraEditor(item.id)}
                                  disabled={!selectedForPdf}
                                >
                                  {extraEditorOpen ? 'Cerrar' : hasExtraDetail ? 'Con detalle' : '+ Detalle'}
                                </button>
                              </td>
                              <td className="mono cell-strong" style={{ textAlign: 'right' }}>
                                {formatMoney(item.final_price)}
                              </td>
                            </tr>
                            {selectedForPdf && extraEditorOpen && (
                              <tr className="pdf-extra-detail-row">
                                <td></td>
                                <td colSpan={5}>
                                  <label className="form-label">Extra en detalle para este PDF</label>
                                  <textarea
                                    className="form-input"
                                    value={pdfExtraDetails[item.id] || ''}
                                    onChange={(e) => updatePdfExtraDetail(item.id, e.target.value)}
                                    placeholder="Ej: entrega urgente, plastificado, terminacion especial"
                                    rows={2}
                                    maxLength={500}
                                  />
                                  <div className="input-hint">
                                    No modifica el presupuesto ni el producto. Solo se agrega en este PDF.
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setPdfDialogOpen(false)}>
                  Cancelar
                </button>
                <button
                  className="btn-primary-inline"
                  onClick={runPdfGeneration}
                  disabled={pdfLoading || pdfOptionsLoading || selectedPdfBudgetIds.length === 0}
                >
                  {pdfLoading
                    ? 'Generando...'
                    : `${pdfAction === 'print' ? 'Imprimir' : 'Descargar'} ${pdfIncludeVat ? 'con IVA' : 'sin IVA'}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {productionDialogOpen && (
          <div className="modal-backdrop" onClick={() => setProductionDialogOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Enviar a produccion</h3>
                <button className="btn-remove" onClick={() => setProductionDialogOpen(false)}>x</button>
              </div>
              <div className="modal-body">
                <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                  Presupuesto #{String(budget.budget_number).padStart(4, '0')} - {budget.product_snapshot?.name || '-'}
                </p>
                <div className="form-group">
                  <label className="form-label">Trabajador *</label>
                  <select
                    className="form-input"
                    value={productionWorkerId}
                    onChange={(e) => setProductionWorkerId(e.target.value)}
                    autoFocus
                  >
                    <option value="">Seleccionar trabajador</option>
                    {workers.map(worker => (
                      <option key={worker.id} value={worker.id}>{worker.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prioridad</label>
                  <select
                    className="form-input"
                    value={productionPriority}
                    onChange={(e) => setProductionPriority(e.target.value)}
                  >
                    {PRODUCTION_PRIORITIES.map(priority => (
                      <option key={priority.key} value={priority.key}>{priority.label}</option>
                    ))}
                  </select>
                </div>
                {workers.length === 0 && (
                  <div className="login-error">Primero crea un trabajador en Configuracion.</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setProductionDialogOpen(false)}>
                  Cancelar
                </button>
                <button
                  className="btn-primary-inline"
                  onClick={confirmSendToProduction}
                  disabled={sendingProduction || !productionWorkerId}
                >
                  {sendingProduction ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDialog
          open={!!statusConfirm}
          title={statusConfirm === 'approved' ? 'Aprobar presupuesto' : 'Volver a pendiente'}
          message={
            statusConfirm === 'approved'
              ? `¿Estas seguro de aprobar el presupuesto #${budget.budget_number}?`
              : `¿Estas seguro de devolver el presupuesto #${budget.budget_number} a pendientes?`
          }
          confirmLabel={statusConfirm === 'approved' ? 'Aprobar' : 'Volver a pendiente'}
          loading={statusLoading}
          onConfirm={confirmStatusChange}
          onCancel={() => setStatusConfirm(null)}
        />
        <ConfirmDialog
          open={deleteConfirmOpen}
          title="Eliminar presupuesto"
          message={`¿Estas seguro de eliminar el presupuesto #${budget.budget_number}?`}
          confirmLabel="Eliminar"
          loading={deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirmOpen(false)}
        />
      </main>
    </div>
  );
};

export default BudgetDetail;
